/**
 * Trial Signup Form with Stripe Card Verification
 * Collects card details for trial activation (charged after 7 days)
 */
import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  CreditCard, Shield, Check, AlertTriangle, Loader2, 
  Calendar, Lock, Sparkles, X
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getDeviceFingerprint } from '../services/fingerprint';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Initialize Stripe (publishable key from env or fallback to test)
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_placeholder'
);

// Card element styles
const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#1e293b',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      '::placeholder': {
        color: '#94a3b8',
      },
      iconColor: '#4f46e5',
    },
    invalid: {
      color: '#dc2626',
      iconColor: '#dc2626',
    },
  },
  hidePostalCode: true,
};


/**
 * Inner form component that uses Stripe hooks
 */
const TrialCardForm = ({ 
  onSuccess, 
  onCancel, 
  planName = "Plan Top",
  planPrice = 29.99,
  trialDays = 7
}) => {
  const stripe = useStripe();
  const elements = useElements();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [fingerprint, setFingerprint] = useState(null);
  const [fingerprintLoading, setFingerprintLoading] = useState(true);
  const [eligibilityChecked, setEligibilityChecked] = useState(false);
  const [notEligible, setNotEligible] = useState(false);
  const [notEligibleReason, setNotEligibleReason] = useState('');

  useEffect(() => {
    checkEligibility();
  }, []);

  const checkEligibility = async () => {
    setFingerprintLoading(true);
    try {
      const fp = await getDeviceFingerprint();
      setFingerprint(fp);

      // Check trial eligibility
      const response = await axios.post(
        `${API}/subscriptions/check-trial-eligibility?fingerprint_hash=${fp.visitorId}`
      );

      if (!response.data.eligible) {
        setNotEligible(true);
        setNotEligibleReason(response.data.reason || 'No eres elegible para la prueba gratuita');
      }
      
      setEligibilityChecked(true);
    } catch (err) {
      console.error('Error checking eligibility:', err);
      setEligibilityChecked(true);
    } finally {
      setFingerprintLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements || !fingerprint) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create payment method
      const cardElement = elements.getElement(CardElement);
      const { error: methodError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (methodError) {
        setError(methodError.message);
        setLoading(false);
        return;
      }

      // Send to backend to setup trial
      const token = localStorage.getItem('session_token');
      const response = await axios.post(
        `${API}/subscriptions/setup-trial`,
        {
          fingerprint_hash: fingerprint.visitorId,
          payment_method_id: paymentMethod.id
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(`¡${trialDays} días de prueba gratuita activados!`);
        onSuccess?.(response.data);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Error al procesar el pago';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCardChange = (event) => {
    setCardComplete(event.complete);
    if (event.error) {
      setError(event.error.message);
    } else {
      setError(null);
    }
  };

  // Show loading while checking fingerprint
  if (fingerprintLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-indigo-600 animate-spin mb-4" />
        <p className="text-slate-600">Verificando elegibilidad...</p>
      </div>
    );
  }

  // Show not eligible message
  if (notEligible) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-red-800 mb-2">
              Prueba gratuita no disponible
            </h3>
            <p className="text-red-700 mb-4">{notEligibleReason}</p>
            <p className="text-sm text-red-600 mb-4">
              La prueba gratuita solo está disponible una vez por persona. 
              Si ya la utilizaste, puedes suscribirte directamente a un plan.
            </p>
            <div className="flex gap-3">
              <Button
                onClick={() => window.location.href = '/pricing'}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                Ver Planes
              </Button>
              <Button variant="outline" onClick={onCancel}>
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Trial Info Banner */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h3 className="font-bold text-indigo-900">{trialDays} días de prueba gratuita</h3>
            <p className="text-sm text-indigo-700">{planName} - €{planPrice}/mes después del trial</p>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2 text-indigo-700">
            <Check className="w-4 h-4 text-emerald-500" />
            <span>Acceso completo</span>
          </div>
          <div className="flex items-center gap-2 text-indigo-700">
            <Check className="w-4 h-4 text-emerald-500" />
            <span>Cancela cuando quieras</span>
          </div>
          <div className="flex items-center gap-2 text-indigo-700">
            <Calendar className="w-4 h-4 text-emerald-500" />
            <span>Sin cargos hasta el día {trialDays}</span>
          </div>
        </div>
      </div>

      {/* Card Input */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          <CreditCard className="w-4 h-4 inline mr-2" />
          Datos de la tarjeta
        </label>
        <div className="border border-slate-300 rounded-lg p-4 bg-white focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-indigo-500">
          <CardElement 
            options={CARD_ELEMENT_OPTIONS}
            onChange={handleCardChange}
          />
        </div>
        <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
          <Lock className="w-3 h-3" />
          Tus datos están protegidos con encriptación SSL
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Security Note */}
      <div className="bg-slate-50 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-slate-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-slate-600">
            <p className="font-medium mb-1">¿Por qué pedimos la tarjeta?</p>
            <p>
              Verificamos tu tarjeta para garantizar una experiencia sin interrupciones. 
              <strong> No se realizará ningún cargo</strong> hasta que finalice tu período de prueba de {trialDays} días.
              Puedes cancelar en cualquier momento.
            </p>
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex gap-3">
        <Button
          type="submit"
          disabled={!stripe || !cardComplete || loading}
          className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white py-6 text-lg font-bold rounded-xl"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Procesando...
            </>
          ) : (
            <>
              <Sparkles className="w-5 h-5 mr-2" />
              Comenzar Prueba Gratuita
            </>
          )}
        </Button>
        
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="px-6"
          >
            <X className="w-5 h-5" />
          </Button>
        )}
      </div>

      {/* Terms */}
      <p className="text-xs text-center text-slate-500">
        Al continuar, aceptas nuestros{' '}
        <a href="/legal" className="text-indigo-600 hover:underline">Términos de Servicio</a>
        {' '}y{' '}
        <a href="/privacy" className="text-indigo-600 hover:underline">Política de Privacidad</a>.
        Se cobrará €{planPrice}/mes después de los {trialDays} días de prueba.
      </p>
    </form>
  );
};


/**
 * Main wrapper component with Stripe Elements provider
 */
const TrialSignupForm = (props) => {
  return (
    <Elements stripe={stripePromise}>
      <TrialCardForm {...props} />
    </Elements>
  );
};


/**
 * Modal version of the trial signup form
 */
export const TrialSignupModal = ({ 
  isOpen, 
  onClose, 
  onSuccess,
  planName = "Plan Top",
  planPrice = 29.99,
  trialDays = 7
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-slate-900">
              Activa tu Prueba Gratuita
            </h2>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>
          
          <TrialSignupForm 
            onSuccess={(data) => {
              onSuccess?.(data);
              onClose();
            }}
            onCancel={onClose}
            planName={planName}
            planPrice={planPrice}
            trialDays={trialDays}
          />
        </div>
      </div>
    </div>
  );
};


export default TrialSignupForm;
